"use client";
import ShareButton from "@/components/views/blog/details/ShareButton";
import { Link } from "@/components/CustomLink";
import { ROUTES } from "@/@core/constants/routes";
import { useTranslation } from "@/hooks/translationContext";
import capitalizeWords from "@/lib/caplitalize-words";
import { usePlausible } from "next-plausible";
import {
  CLICK_QUOTE_EVENT_SECTIONS,
  PLAUSIBLE_GOAL_KEYS,
} from "@/@core/constants/plausible";
import { BlogListType } from "@/@core/enums/blog.enum";
import MapPinIcon from "@heroicons/react/24/outline/MapPinIcon";
import { useNearbyNavigation } from "@/hooks/useNearbyNavigation";
import { useSite } from "@/lib/hooks/site";
import { useIsAskQuestionEnabled } from "@/hooks/useIsAskQuestionEnabled";

export default function BlogDetailHeader({ blog }: { blog: Blog }) {
  const { t } = useTranslation();
  const plausible = usePlausible();
  const { goToNearby } = useNearbyNavigation();
  const site = useSite();
  const isAskQuestionEnabled = useIsAskQuestionEnabled();
  const rawViewButtonTitle = t("blog-detail-venue-view-button", {
    defaultValue: "İşletmeleri Gör",
  });
  const viewButtonTitle =
    site?.domain?.includes("ankaraanaokulu")
      ? t("blog-detail-venue-list-button")
      : rawViewButtonTitle;

  const sendPlausibleEvent = () => {
    plausible(PLAUSIBLE_GOAL_KEYS.CLICK_QUOTE_EVENT, {
      props: {
        section: CLICK_QUOTE_EVENT_SECTIONS.BLOG_DETAIL,
      },
    });
  };

  const handleViewClick = () => {
    const targetId =
      blog.list_type === BlogListType.FIRST_VENUE
        ? "blog-detail"
        : "related-venues";
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleClickCommentButton = () => {
    const element = document.getElementById("comment-form");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  return (
    <header className="flex max-xl:flex-col items-start">
      <div className="flex border-b border-stone-50 flex-wrap md:py-4 text-black w-full gap-4 items-center md:justify-between">
        <h1 className="text-ellipsis line-clamp-3 max-md:text-[26px] text-[32px] font-bold text-black lg:max-w-[895px]">
          {capitalizeWords(blog?.descriptions?.[0]?.title)}
        </h1>

        <div className="hidden xl:flex items-center gap-4">
          <button
            onClick={goToNearby}
            className="flex flex-col gap-1 items-center text-[13.5px] font-medium text-black"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <MapPinIcon className="size-5" />
            Yakındakiler
          </button>
          <ShareButton id={45} />
        </div>
      </div>

      {/* Mobile Buttons - Figma Design */}
      <div className="flex max-xl:justify-between border-b border-gray-200 w-full gap-1.5 xl:hidden py-4">
        {/* Yazıyı Oku Button - White with border */}
        <button
          onClick={() => handleViewClick()}
          className="flex items-center justify-center px-2 py-2 bg-white border border-[#d9d9d9] rounded-[9px] shadow-[0px_0px_2.4px_0px_rgba(99,99,99,0.25)]"
        >
          <span className="text-[11px] font-medium text-black whitespace-nowrap" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            {blog.list_type === BlogListType.FIRST_TEXT
              ? viewButtonTitle
              : t("blog-read-button", { defaultValue: "Yazıyı Oku" })}
          </span>
        </button>

        {isAskQuestionEnabled && (
          <>
            {/* Ücretsiz Teklif Al Button - Red gradient */}
            <Link
              href={ROUTES.CONTACT_WITH_VENUES}
              onClick={sendPlausibleEvent}
              className="flex-1 min-w-0 flex items-center justify-center px-2 py-2 bg-gradient-to-r from-[#d90561] to-[#e41b48] rounded-[9px] shadow-[0px_0px_2.4px_0px_rgba(99,99,99,0.25)]"
            >
              <span className="text-[11px] font-medium text-white whitespace-nowrap" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                {t("free-quote-button", { defaultValue: "Ücretsiz Teklif Al" })}
              </span>
            </Link>

            {/* Soru Sor Button - Teal/Turquoise */}
            <button
              onClick={handleClickCommentButton}
              className="flex items-center justify-center px-2 py-2 bg-[#14bcae] rounded-[9px] shadow-[0px_0px_2.4px_0px_rgba(99,99,99,0.25)]"
            >
              <span className="text-[11px] font-medium text-white whitespace-nowrap" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                {t("ask-question-button", { defaultValue: "Soru Sor" })}
              </span>
            </button>
          </>
        )}
      </div>
    </header>
  );
}
